import type {
  Asset,
  AssetFormData,
  AssetMetadata,
  FormattedAsset,
} from '../types/asset';
import type { KPICalculationOptions } from '../types/kpi';
// src/services/AssetService.ts
import { apiClient } from './ApiClient';

export const AssetService = {
  // Get all assets
  getAssets: async (): Promise<Asset[]> => {
    try {
      const response = await apiClient.get('/assets');
      return response.data;
    } catch (error) {
      console.error('Error fetching assets:', error);
      throw error;
    }
  },

  // Get a single asset by ID or slug
  getAsset: async (identifier: string): Promise<Asset> => {
    try {
      const response = await apiClient.get(`/assets/${identifier}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching asset ${identifier}:`, error);
      throw error;
    }
  },

  // Get assets by type
  getAssetsByType: async (assetType: string): Promise<Asset[]> => {
    try {
      const response = await apiClient.get(`/assets/type/${assetType}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching assets of type ${assetType}:`, error);
      throw error;
    }
  },

  // Create a new asset
  createAsset: async (assetData: AssetFormData): Promise<Asset> => {
    try {
      const response = await apiClient.post('/assets', assetData);
      return response.data;
    } catch (error) {
      console.error('Error creating asset:', error);
      throw error;
    }
  },

  // Update an asset
  updateAsset: async (
    identifier: string | number,
    assetData: Partial<AssetFormData>,
  ): Promise<Asset> => {
    try {
      console.log(`Updating asset ${identifier} with data:`, assetData);
      const response = await apiClient.put(`/assets/${identifier}`, assetData);
      return response.data;
    } catch (error) {
      console.error(`Error updating asset ${identifier}:`, error);
      throw error;
    }
  },

  // Delete an asset
  deleteAsset: async (identifier: string): Promise<void> => {
    try {
      await apiClient.delete(`/assets/${identifier}`);
    } catch (error) {
      console.error(`Error deleting asset ${identifier}:`, error);
      throw error;
    }
  },

  // Add a contact to an asset
  addContactToAsset: async (
    assetId: string,
    contactId: number,
    role: string,
  ): Promise<void> => {
    try {
      await apiClient.post(`/assets/${assetId}/contacts`, {
        contact_id: contactId,
        role,
      });
    } catch (error) {
      console.error(`Error adding contact to asset ${assetId}:`, error);
      throw error;
    }
  },

  // Remove a contact from an asset
  removeContactFromAsset: async (
    assetId: string,
    contactId: number,
  ): Promise<void> => {
    try {
      await apiClient.delete(`/assets/${assetId}/contacts/${contactId}`);
    } catch (error) {
      console.error(`Error removing contact from asset ${assetId}:`, error);
      throw error;
    }
  },

  async lookupAssetByAddress(
    address: string,
  ): Promise<FormattedAsset[] | null> {
    try {
      const response = await apiClient.get('/assets/lookup', {
        params: { address },
      });
      if (!response.data.success || !response.data.data) {
        return null;
      }
      return response.data.data;
    } catch (error) {
      console.error('Error looking up asset by address:', error);
      return null;
    }
  },

  async lookupAsset(address: string) {
    const response = await apiClient.get<{
      success: boolean;
      data: FormattedAsset[];
    }>('/assets/lookup-asset', { params: { address } });
    return response.data;
  },

  async lookupParcel(parcelID: string, zpid: string) {
    const response = await apiClient.get<{
      success: boolean;
      data: AssetMetadata;
    }>('/assets/lookup-parcel', { params: { parcelID, zpid } });
    return response.data;
  },

  // Enhanced KPI methods with calculation options
  getAssetKpis: async (
    assetId: string,
    period = 'annual',
    options: KPICalculationOptions = {},
  ) => {
    try {
      const params = {
        period,
        includeVacancy: options.includeVacancy,
        includeAppreciation: options.includeAppreciation,
        ...options.customParameters,
      };

      const response = await apiClient.get(
        `/kpi-preferences/assets/${assetId}/calculate`,
        {
          params,
        },
      );
      return response.data;
    } catch (error) {
      console.error(`Error fetching KPIs for asset ${assetId}:`, error);
      throw error;
    }
  },

  // Fetch comparative KPIs for an asset
  getComparativeKpis: async (
    assetId: string,
    period = 'annual',
    options: KPICalculationOptions = {},
  ) => {
    try {
      const params = {
        period,
        includeVacancy: options.includeVacancy,
        includeAppreciation: options.includeAppreciation,
        ...options.customParameters,
      };

      const response = await apiClient.get(
        `/kpi-preferences/assets/${assetId}/calculate`,
        { params },
      );
      return response.data;
    } catch (error) {
      console.error(
        `Error fetching comparative KPIs for asset ${assetId}:`,
        error,
      );
      throw error;
    }
  },

  // Fetch portfolio KPIs
  getPortfolioKpis: async (
    period = 'annual',
    options: KPICalculationOptions = {},
  ) => {
    try {
      const params = {
        period,
        includeVacancy: options.includeVacancy,
        includeAppreciation: options.includeAppreciation,
        ...options.customParameters,
      };

      const response = await apiClient.get('/kpis/portfolio/kpis', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching portfolio KPIs:', error);
      throw error;
    }
  },

  // Fetch KPI comparison for multiple assets
  getKpiComparison: async (
    assetIds: string[],
    period = 'annual',
    options: KPICalculationOptions = {},
  ) => {
    try {
      const params = {
        period,
        includeVacancy: options.includeVacancy,
        includeAppreciation: options.includeAppreciation,
        assetIds: assetIds.join(','),
        ...options.customParameters,
      };

      const response = await apiClient.get('/kpis/comparison', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching KPI comparison:', error);
      throw error;
    }
  },

  // Save KPI snapshot for historical tracking
  saveKpiSnapshot: async (
    assetId: string,
    kpiData: Record<string, any>,
    period = 'annual',
  ) => {
    try {
      const response = await apiClient.post('/kpis/snapshots', {
        asset_id: assetId,
        kpi_data: kpiData,
        period,
      });
      return response.data;
    } catch (error) {
      console.error(`Error saving KPI snapshot for asset ${assetId}:`, error);
      throw error;
    }
  },

  // Get KPI snapshots for historical analysis
  getKpiSnapshots: async (
    assetId: string,
    startDate?: string,
    endDate?: string,
  ) => {
    try {
      const params = {
        asset_id: assetId,
        start_date: startDate,
        end_date: endDate,
      };

      const response = await apiClient.get('/kpis/snapshots', { params });
      return response.data;
    } catch (error) {
      console.error(
        `Error fetching KPI snapshots for asset ${assetId}:`,
        error,
      );
      throw error;
    }
  },

  // Add market comparison data
  addMarketComparison: async (
    assetId: string,
    marketData: {
      market_cap_rate?: number;
      market_rent?: number;
      market_price_per_sqft?: number;
      neighborhood_metrics?: Record<string, any>;
      data_source?: string;
    },
  ) => {
    try {
      const response = await apiClient.post('/kpis/market-comparison', {
        asset_id: assetId,
        ...marketData,
      });
      return response.data;
    } catch (error) {
      console.error(
        `Error adding market comparison for asset ${assetId}:`,
        error,
      );
      throw error;
    }
  },

  // Get market comparison data
  getMarketComparison: async (assetId: string) => {
    try {
      const response = await apiClient.get(
        `/kpis/market-comparison/${assetId}`,
      );
      return response.data;
    } catch (error) {
      console.error(
        `Error fetching market comparison for asset ${assetId}:`,
        error,
      );
      throw error;
    }
  },
};

export default AssetService;
